import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Phone, Building, FileText } from 'lucide-react';
import { useLead, useUpdateLead, useLeadActivities } from '../hooks/useLeads';
import { Badge, getCuisineBadgeVariant } from '../components/common/Badge';
import { Button } from '../components/common/Button';
import { useState } from 'react';
import type { Lead, LeadActivity } from '../api/client';

const statusOptions = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'closed', label: 'Closed' },
];

export function LeadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const leadId = parseInt(id || '0', 10);

  const { data: lead, isLoading, error } = useLead(leadId);
  const { data: activitiesData } = useLeadActivities(leadId);
  const updateLead = useUpdateLead();

  const [notes, setNotes] = useState('');
  const [hasNotesChanged, setHasNotesChanged] = useState(false);

  // Initialize notes when lead loads
  if (lead && !hasNotesChanged && notes !== (lead.notes || '')) {
    setNotes(lead.notes || '');
  }

  const handleStatusChange = async (newStatus: Lead['status']) => {
    if (!lead) return;
    await updateLead.mutateAsync({ id: lead.id, data: { status: newStatus } });
  };

  const handleSaveNotes = async () => {
    if (!lead) return;
    await updateLead.mutateAsync({ id: lead.id, data: { notes } });
    setHasNotesChanged(false);
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-brand-primary border-r-transparent" />
        <p className="mt-4 text-font-secondary">Loading lead...</p>
      </div>
    );
  }

  if (error || !lead) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Lead not found or failed to load.</p>
        <Button variant="outline" onClick={() => navigate(-1)} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center space-x-2 text-font-secondary hover:text-brand-primary transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>Back to Leads</span>
      </button>

      {/* Header */}
      <div className="card p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-serif font-semibold text-font-primary mb-2">
              {lead.name}
            </h1>
            <div className="flex flex-wrap gap-2">
              {lead.is_new && <Badge variant="new">New</Badge>}
              {lead.cuisine_detected && (
                <Badge variant={getCuisineBadgeVariant(lead.cuisine_detected)}>
                  {lead.cuisine_detected}
                </Badge>
              )}
              {lead.source_name && <Badge>{lead.source_name}</Badge>}
            </div>
          </div>

          {/* Status buttons */}
          <div className="flex flex-wrap gap-2">
            {statusOptions.map((option) => (
              <Button
                key={option.value}
                variant={lead.status === option.value ? 'primary' : 'outline'}
                size="sm"
                onClick={() => handleStatusChange(option.value as Lead['status'])}
                isLoading={updateLead.isPending}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Contact Info */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-font-primary mb-4">
              Contact Information
            </h2>
            <div className="space-y-4">
              {lead.address && (
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-font-secondary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-font-primary">{lead.address}</p>
                    <p className="text-font-secondary text-sm">
                      {[lead.city, lead.state, lead.zipcode].filter(Boolean).join(', ')}
                    </p>
                  </div>
                </div>
              )}

              {lead.phone && (
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-font-secondary" />
                  <a
                    href={`tel:${lead.phone}`}
                    className="text-brand-primary hover:underline"
                  >
                    {lead.phone}
                  </a>
                </div>
              )}

              {lead.region && (
                <div className="flex items-center space-x-3">
                  <Building className="w-5 h-5 text-font-secondary" />
                  <span className="text-font-primary">{lead.region}</span>
                </div>
              )}
            </div>
          </div>

          {/* License Info */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-font-primary mb-4">
              License Information
            </h2>
            <div className="space-y-4">
              {lead.license_type && (
                <div className="flex items-start space-x-3">
                  <FileText className="w-5 h-5 text-font-secondary flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-font-primary">{lead.license_type}</p>
                    {lead.license_status && (
                      <p className="text-font-secondary text-sm">{lead.license_status}</p>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-font-secondary">First Seen</p>
                  <p className="text-font-primary">{formatDate(lead.first_seen_at)}</p>
                </div>
                <div>
                  <p className="text-font-secondary">Effective Date</p>
                  <p className="text-font-primary">{formatDate(lead.effective_date)}</p>
                </div>
                <div>
                  <p className="text-font-secondary">Inspection Date</p>
                  <p className="text-font-primary">{formatDate(lead.inspection_date)}</p>
                </div>
                <div>
                  <p className="text-font-secondary">External ID</p>
                  <p className="text-font-primary font-mono text-xs">{lead.external_id}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-font-primary">Notes</h2>
              {hasNotesChanged && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveNotes}
                  isLoading={updateLead.isPending}
                >
                  Save Notes
                </Button>
              )}
            </div>
            <textarea
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setHasNotesChanged(true);
              }}
              className="input min-h-[150px] resize-y"
              placeholder="Add notes about this lead..."
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Activity Log */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-font-primary mb-4">
              Activity Log
            </h2>
            {activitiesData?.activities && activitiesData.activities.length > 0 ? (
              <div className="space-y-4">
                {activitiesData.activities.map((activity: LeadActivity) => (
                  <div
                    key={activity.id}
                    className="pb-4 border-b border-card-border last:border-0 last:pb-0"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-font-primary">
                        {activity.action.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                      </span>
                      <span className="text-xs text-font-secondary">
                        {new Date(activity.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-sm text-font-secondary">
                      {activity.user_email}
                    </p>
                    {activity.new_value && (
                      <p className="text-sm text-font-secondary mt-1">
                        {activity.old_value && `${activity.old_value} → `}
                        {activity.new_value}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-font-secondary text-sm">No activity yet.</p>
            )}
          </div>

          {/* Cuisine Keywords */}
          {lead.cuisine_keywords && lead.cuisine_keywords.length > 0 && (
            <div className="card p-6">
              <h2 className="text-lg font-semibold text-font-primary mb-4">
                Matched Keywords
              </h2>
              <div className="flex flex-wrap gap-2">
                {lead.cuisine_keywords.map((keyword, i) => (
                  <Badge key={i} variant="default">
                    {keyword}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
